"use client";

import { Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const inquiryOptions = ["General Inquiry", "Editorial Feedback", "Story Tip / Grassroots Lead", "Partnership Opportunity", "Other"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-b border-border py-12">
        <p className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-4">
          {"Submission Received"}
        </p>
        <p className="text-foreground text-lg leading-[1.55] mb-8 font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="h-12 px-6 border border-foreground bg-transparent text-foreground text-xs font-medium uppercase tracking-[0.04em] hover:bg-primary hover:text-primary-foreground transition-colors duration-200">
          {"Send another message"}
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0">
      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select a category"
    }} render={({
      field
    }) => <FormItem data-index={0} className="pb-8 border-b border-border">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-3">
              {"Category"}
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full h-auto border-0 border-b border-transparent bg-transparent px-0 py-2 text-base text-foreground font-default focus:ring-0 focus-visible:ring-0 focus-visible:border-transparent">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="">
                  {inquiryOptions.map((opt, i) => <SelectItem key={opt} data-index={i} value={opt}>
                      {opt}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <p className="text-xs text-muted-foreground mt-2 font-default">
              {"Help us route your message to the right desk."}
            </p>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="pt-8 pb-8 border-b border-border">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-3">
              {"Full Name"}
            </FormLabel>
            <FormControl>
              <Input placeholder="Enter your name" className="h-auto border-0 bg-transparent px-0 py-2 text-base text-foreground font-default focus-visible:ring-0 focus-visible:border-0" {...field} />
            </FormControl>
            <p className="text-xs text-muted-foreground mt-2 font-default">
              {"How should we address you?"}
            </p>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="pt-8 pb-8 border-b border-border">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-3">
              {"Email Address"}
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="name@example.com" className="h-auto border-0 bg-transparent px-0 py-2 text-base text-foreground font-default focus-visible:ring-0 focus-visible:border-0" {...field} />
            </FormControl>
            <p className="text-xs text-muted-foreground mt-2 font-default">
              {"We will use this to contact you."}
            </p>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="organization" render={({
      field
    }) => <FormItem className="pt-8 pb-8 border-b border-border">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-3">
              {"Organization"}
            </FormLabel>
            <FormControl>
              <Input placeholder="Optional" className="h-auto border-0 bg-transparent px-0 py-2 text-base text-foreground font-default focus-visible:ring-0 focus-visible:border-0" {...field} />
            </FormControl>
            <p className="text-xs text-muted-foreground mt-2 font-default">
              {"Your affiliation, if applicable."}
            </p>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem className="pt-8 pb-8 border-b border-border">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-3">
              {"Subject"}
            </FormLabel>
            <FormControl>
              <Input placeholder="What is this regarding?" className="h-auto border-0 bg-transparent px-0 py-2 text-base text-foreground font-default focus-visible:ring-0 focus-visible:border-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="pt-8 pb-8 border-b border-border">
            <FormLabel className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mb-3">
              {"Message"}
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Share your story or inquiry here..." className="border-0 bg-transparent px-0 py-2 text-base text-foreground font-default focus-visible:ring-0 focus-visible:border-0 resize-none min-h-[140px]" {...field} />
            </FormControl>
            <p className="text-xs text-muted-foreground mt-2 font-default">
              {"Provide as much detail as possible."}
            </p>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="pt-6">
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}

      <div className="pt-12">
        <Button type="submit" disabled={isSubmitting} className="group h-12 px-6 bg-foreground text-background text-xs font-medium uppercase tracking-[0.04em] hover:bg-primary transition-all duration-220">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              {"Sending..."}
            </> : "Submit Inquiry"}
        </Button>
        <p className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground mt-6">
          {"We aim to respond within 5 business days."}
        </p>
      </div>
    </Form>;
}
export default function Contact() {
  return <section data-section-id="10745" id="reach-the-newsroom" className="bg-background text-foreground py-32 md:py-44">
      <div className="max-w-[1440px] mx-auto px-5 md:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-6">
          <div className="lg:col-span-5">
            <h2 className="font-default text-foreground font-semibold leading-[1.02] tracking-[-0.03em] text-[clamp(2.25rem,4.2vw,4rem)]">
              {"Reach the "}
              <span className="relative inline-block">
                {"newsroom"}
                <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1 h-[2px] bg-primary" />
              </span>
              {"."}
            </h2>

            <p className="mt-8 text-muted-foreground font-default text-[clamp(1.0625rem,1.1vw,1.1875rem)] leading-[1.55] tracking-[-0.005em] max-w-[42ch]">
              {"Inclusive Voices News is committed to transparency and community dialogue. Use this portal to submit story tips, provide feedback, or inquire about our editorial standards."}
            </p>

            <div className="mt-16">
              <p className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
                {"For community guidelines, see "}
                <Link to="/OurStoryPage" className="text-foreground relative inline-block hover:opacity-80 transition-opacity">
                  {"Our Mission"}
                  <span aria-hidden="true" className="absolute left-0 right-0 -bottom-[2px] h-[1px] bg-primary" />
                </Link>
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <FormProvider formKey="reach_newsroom_form_connect" formId="reach_newsroom_form_connect" sectionName="reach-the-newsroom" defaultValues={{
            inquiry_type: "",
            name: "",
            email: "",
            organization: "",
            subject: "",
            message: ""
          }} validationRules={{
            inquiry_type: {
              required: true
            },
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Inquiry" successMessage="Thank you for reaching out. Your message has been received by our editorial team." errorMessage="We encountered an error. Please try again later.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}